import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import InteractiveStory from '../components/InteractiveStory';
import { RecommendationProvider, useRecommendation } from '../context/RecommendationContext';
import { extractDocId, isValidDocId } from '../utils/docId';
import { retrieveDocument } from '../utils/documentPersistence';
import { normalizeSystemRecOutput } from '@core/normalizeOutput';

interface CustomerStoryContentProps {
  docId: string;
}

function resolveDocId(pathDocId: string | undefined, searchParams: URLSearchParams): string | null {
  if (pathDocId) {
    const extracted = extractDocId(pathDocId);
    if (extracted && isValidDocId(extracted)) {
      return extracted;
    }
  }

  const queryDocId = searchParams.get('docId');
  if (queryDocId) {
    const extracted = extractDocId(queryDocId);
    if (extracted && isValidDocId(extracted)) {
      return extracted;
    }
  }

  return null;
}

function CustomerStoryContent({ docId }: CustomerStoryContentProps) {
  const { setCollectedData, setRecommendationResult, setSelectedOptionId } = useRecommendation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    setLoading(true);
    retrieveDocument(docId)
      .then((doc) => {
        if (!isMounted) return;

        if (doc.rawInput) {
          setCollectedData({
            ...doc.rawInput,
            documentReference: doc.rawInput.documentReference ?? docId,
          });
        }

        if (doc.engineOutput) {
          setRecommendationResult({
            input: doc.engineOutput.input,
            output: doc.engineOutput.output,
            normalizedOutput: doc.engineOutput.normalizedOutput
              ?? normalizeSystemRecOutput(doc.engineOutput.output, doc.engineOutput.input),
          });
        }

        if (doc.selectedOptionId) {
          setSelectedOptionId(doc.selectedOptionId);
        }

        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err?.message || 'Failed to load document');
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [docId, setCollectedData, setRecommendationResult, setSelectedOptionId]);

  if (loading) {
    return (
      <div className="story-view-page">
        <div className="story-view-container">
          <div className="story-view-content">
            <h2>Loading your tailored story...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="story-view-page">
        <div className="story-view-container">
          <div className="story-view-content" style={{
            padding: '2rem',
            background: '#fef2f2',
            border: '2px solid #fca5a5',
            borderRadius: '8px',
          }}>
            <h2 style={{ color: '#991b1b', marginBottom: '1rem' }}>
              ❌ Unable to load your report
            </h2>
            <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
              {error}
            </p>
            <Link
              to="/story"
              className="btn btn-primary"
              style={{ marginTop: '1rem', display: 'inline-block' }}
            >
              Create a new report →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <InteractiveStory documentCode={docId} />;
}

function CustomerStoryPage() {
  const { docId: pathDocId } = useParams<{ docId?: string }>();
  const [searchParams] = useSearchParams();

  const docId = useMemo(() => resolveDocId(pathDocId, searchParams), [pathDocId, searchParams]);

  if (!docId) {
    return (
      <div className="story-view-page">
        <div className="story-view-container">
          <div className="story-view-content" style={{
            padding: '2rem',
            background: '#fef2f2',
            border: '2px solid #fca5a5',
            borderRadius: '8px',
          }}>
            <h2 style={{ color: '#991b1b', marginBottom: '1rem' }}>
              ❌ Invalid Link Format
            </h2>
            <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
              The link you're trying to access is malformed or in an incorrect format.
              Document IDs should look like: <code>doc_abc123xyz</code>
            </p>
            <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
              If you received this link from a QR code or email, please contact support.
            </p>
            <Link
              to="/story"
              className="btn btn-primary"
              style={{ marginTop: '1rem', display: 'inline-block' }}
            >
              Create a new report →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RecommendationProvider>
      <CustomerStoryContent docId={docId} />
    </RecommendationProvider>
  );
}

export default CustomerStoryPage;
